/**
 * CommercesCard — commerces & services de proximité (OpenStreetMap).
 *
 * Layout : bandeau 4 compteurs (alimentation / restauration / services /
 * santé), puis top 5 par catégorie (le plus proche d'abord). On ne liste
 * pas les 80 items complets dans la carte : l'utilisateur veut "est-ce
 * qu'il y a une boulangerie / un café à moins de 10 min à pied" — pas un
 * inventaire Yelp.
 */

import { useState } from "react";
import type { FicheDoc } from "../../lib/fiches";
import {
  useCommerces,
  type CommerceCategory,
  type CommerceItem,
} from "../../lib/env/commerces";
import { TIER_LABEL } from "../../lib/env/localityTier";
import { EnvCardShell } from "./EnvCardShell";
import { Ic } from "../icons";

type Props = { fiche: FicheDoc };

// Display order + labels + icons. `sante` last because in most French
// neighbourhoods it's the smallest bucket and folds naturally.
const CATEGORY_ORDER: {
  key: CommerceCategory;
  label: string;
  short: string;
  icon: React.ReactNode;
}[] = [
  { key: "alimentation", label: "Alimentation", short: "Alim.", icon: <Ic.Home size={14} /> },
  { key: "restauration", label: "Restauration", short: "Resto", icon: <Ic.Star size={14} /> },
  { key: "services", label: "Services", short: "Serv.", icon: <Ic.Building size={14} /> },
  { key: "sante", label: "Santé", short: "Santé", icon: <Ic.Shield size={14} /> },
];

export function CommercesCard({ fiche }: Props) {
  const state = useCommerces(fiche.id, fiche.geocode);
  const [activeTab, setActiveTab] = useState<CommerceCategory>("alimentation");

  const subtitle =
    state.status === "ready"
      ? `${state.data.items.length} commerces · rayon ${formatRadius(
          state.data.radius_m,
        )} · ${TIER_LABEL[state.data.tier]}`
      : undefined;

  return (
    <EnvCardShell
      icon={<Ic.Wallet size={18} />}
      title="Commerces & services"
      subtitle={subtitle}
      state={state}
      footer={
        <>
          Source : OpenStreetMap · contributions communautaires. Les données
          peuvent avoir un retard de quelques semaines sur le terrain.
        </>
      }
    >
      {state.status === "ready" && (
        <CommercesBody
          counts={state.data.counts}
          items={state.data.items}
          active={activeTab}
          onTab={setActiveTab}
        />
      )}
    </EnvCardShell>
  );
}

// ─── Body ───────────────────────────────────────────────────────────────

function CommercesBody({
  counts,
  items,
  active,
  onTab,
}: {
  counts: Record<CommerceCategory, number>;
  items: CommerceItem[];
  active: CommerceCategory;
  onTab: (c: CommerceCategory) => void;
}) {
  const activeItems = items.filter((i) => i.category === active).slice(0, 8);

  return (
    <div>
      {/* Count bandeau — clickable chips acting as tabs */}
      <div className="grid grid-cols-4 gap-1.5">
        {CATEGORY_ORDER.map(({ key, short, icon }) => {
          const selected = active === key;
          const n = counts[key] ?? 0;
          return (
            <button
              key={key}
              type="button"
              onClick={() => onTab(key)}
              className={[
                "rounded-xl px-2.5 py-2 text-left transition-colors",
                selected
                  ? "bg-ink-900 text-paper"
                  : "bg-ink-50 text-ink-800 hover:bg-ink-100 hairline",
              ].join(" ")}
            >
              <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.08em] opacity-80">
                {icon}
                {short}
              </div>
              <div className="text-[20px] font-semibold tabular leading-tight mt-0.5">
                {n}
              </div>
            </button>
          );
        })}
      </div>

      {/* Detail list for the active tab */}
      <div className="mt-4">
        {activeItems.length === 0 ? (
          <div className="text-[12.5px] text-ink-500 py-2">
            Aucun commerce de cette catégorie à proximité.
          </div>
        ) : (
          <ul className="space-y-1">
            {activeItems.map((it) => (
              <CommerceRow key={it.id} item={it} />
            ))}
            {counts[active] > activeItems.length && (
              <li className="text-[11.5px] text-ink-500 pl-0.5">
                + {counts[active] - activeItems.length} autres
              </li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
}

function CommerceRow({ item }: { item: CommerceItem }) {
  return (
    <li className="flex items-center justify-between gap-3 py-1">
      <div className="min-w-0 flex-1">
        <div className="text-[13px] text-ink-900 truncate">{item.name}</div>
        <div className="text-[11px] text-ink-500 capitalize">
          {prettifySubtype(item.subtype)}
        </div>
      </div>
      <span className="text-[11.5px] text-ink-500 tabular shrink-0 w-12 text-right">
        {formatDistance(item.distance_m)}
      </span>
    </li>
  );
}

// ─── Small helpers ──────────────────────────────────────────────────────

function prettifySubtype(s: string): string {
  // "fast_food" → "Fast food", "post_office" → "Post office".
  // OSM tags are English / snake_case — we'd ideally translate (bakery →
  // boulangerie), but a dictionary for 40+ keys is out of scope for this
  // iteration. Keeping the raw term is information-preserving.
  return s.replace(/_/g, " ");
}

function formatDistance(m: number): string {
  if (m < 950) return `${m} m`;
  return `${(m / 1000).toFixed(1).replace(".", ",")} km`;
}

// Variant of formatDistance that rounds whole-km radii — the subtitle
// shows "5 km" instead of "5,0 km" for a cleaner read.
function formatRadius(m: number): string {
  if (m < 1000) return `${m} m`;
  if (m % 1000 === 0) return `${m / 1000} km`;
  return `${(m / 1000).toFixed(1).replace(".", ",")} km`;
}
